import { PRODUCT_TYPE } from "@/constants";
import { compareCurrencyRank } from "@/utils/sortProducts";

export type AccountSummaryBalance = {
  balance: number;
};

export type AccountSummaryProduct = {
  code: string;
  resourceType: string;
};

export type EntityScopedBalanceEntry = {
  code: string;
  balance: number;
  resourceType: string;
};

export type AccountSummaryItem =
  | { type: typeof PRODUCT_TYPE.CURRENCIES; currency: string; balance: number }
  | {
      type: typeof PRODUCT_TYPE.DIGITAL_ASSETS;
      product: string;
      balance: number;
    };

type EntityScopeFlags = {
  hasCurrencies: boolean;
  hasDigitalAssets: boolean;
};

type BuildAccountSummaryItemsParams = {
  balances: Record<string, AccountSummaryBalance>;
  products: AccountSummaryProduct[] | undefined;
  hasCurrencies: boolean;
  hasDigitalAssets: boolean;
  baseCurrency: string;
  convert: (amount: number, currency: string) => number;
};

type ShouldShowViewAllAccountsCardParams = {
  summaryCodes: string[];
  allAccountsPageCodes: string[];
};

type AccountPageCodeSource = {
  code: string;
};

/** Balances from /balances, filtered by booking-entity product scope (UP fiat / UT crypto). */
export function filterEntityScopedBalances(
  balances: Record<string, AccountSummaryBalance>,
  products: AccountSummaryProduct[] | undefined,
  { hasCurrencies, hasDigitalAssets }: EntityScopeFlags
): EntityScopedBalanceEntry[] {
  if (!products?.length) return [];

  const productByCode = new Map(
    products.map((product) => [product.code, product])
  );
  const entries: EntityScopedBalanceEntry[] = [];

  for (const [code, balanceInfo] of Object.entries(balances)) {
    const product = productByCode.get(code);
    if (!product) continue;

    const isCurrency = product.resourceType === PRODUCT_TYPE.CURRENCIES;
    if (isCurrency && !hasCurrencies) continue;
    if (!isCurrency && !hasDigitalAssets) continue;

    entries.push({
      code,
      balance: balanceInfo.balance,
      resourceType: product.resourceType,
    });
  }

  return entries;
}

export function getAccountSummaryItemCode(item: AccountSummaryItem): string {
  return item.type === PRODUCT_TYPE.CURRENCIES ? item.currency : item.product;
}

function toSummaryItem(entry: EntityScopedBalanceEntry): AccountSummaryItem {
  if (entry.resourceType === PRODUCT_TYPE.CURRENCIES) {
    return {
      type: PRODUCT_TYPE.CURRENCIES,
      currency: entry.code,
      balance: entry.balance,
    };
  }

  return {
    type: PRODUCT_TYPE.DIGITAL_ASSETS,
    product: entry.code,
    balance: entry.balance,
  };
}

function sortSummaryItems(
  items: AccountSummaryItem[],
  baseCurrency: string,
  convert: (amount: number, currency: string) => number
): AccountSummaryItem[] {
  return [...items].sort((a, b) => {
    const codeA = getAccountSummaryItemCode(a);
    const codeB = getAccountSummaryItemCode(b);

    if (codeA === baseCurrency) return -1;
    if (codeB === baseCurrency) return 1;

    const valueA = convert(a.balance, codeA);
    const valueB = convert(b.balance, codeB);
    if (valueA !== valueB) return valueB - valueA;

    return compareCurrencyRank(
      { code: codeA, type: a.type },
      { code: codeB, type: b.type }
    );
  });
}

/** Dashboard account-summary carousel: exactly the /balances map, entity-scoped. */
export function buildAccountSummaryItems({
  balances,
  products,
  hasCurrencies,
  hasDigitalAssets,
  baseCurrency,
  convert,
}: BuildAccountSummaryItemsParams): AccountSummaryItem[] {
  const entries = filterEntityScopedBalances(balances, products, {
    hasCurrencies,
    hasDigitalAssets,
  });

  return sortSummaryItems(entries.map(toSummaryItem), baseCurrency, convert);
}

/** Union of product codes shown on the /accounts page. */
export function collectAccountPageCodes(
  accounts: AccountPageCodeSource[],
  availableAccounts: AccountPageCodeSource[],
  tradableOnly: AccountPageCodeSource[]
): string[] {
  const codes = new Set<string>();

  for (const entry of [...accounts, ...availableAccounts, ...tradableOnly]) {
    codes.add(entry.code);
  }

  return [...codes];
}

/** Collect summary item codes for view-all comparisons. */
export function collectAccountSummaryCodes(
  items: AccountSummaryItem[]
): string[] {
  return items.map(getAccountSummaryItemCode);
}

/**
 * Show the trailing "View all accounts" card when /accounts lists any product
 * code not already represented on the balance-only summary strip.
 */
export function shouldShowViewAllAccountsCard({
  summaryCodes,
  allAccountsPageCodes,
}: ShouldShowViewAllAccountsCardParams): boolean {
  if (allAccountsPageCodes.length === 0) return false;

  const summaryCodeSet = new Set(summaryCodes);
  return allAccountsPageCodes.some((code) => !summaryCodeSet.has(code));
}
